import React, { useEffect, useState } from 'react'
import {
  addClientAccessToExpertPartner,
  addExpertPartner,
  getExpertPartnersList,
  getPartnerExpertAccess,
  removeClientAccessFromExpertPartner,
  updatePartnerExpertAccess
} from '../../api/expertPartner'
import DataTable from '../../components/UI/table'
import { PHSHeader } from '../../components/UI/header'
import { styles } from './index.sass'
import { Button, Modal, Select, Switch, message } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { getClients } from '../../api'

const { Option } = Select

export const PartnerExpert = () => {
  const [partnerExperts, setPartnerExperts] = useState([])
  const [accessManagement, setAccessManagement] = useState([])
  const [loading, setLoading] = useState(false)
  const [accessLoading, setAccessLoading] = useState(false)
  const [updatingAccessIds, setUpdatingAccessIds] = useState([])
  const [selectedPartnerExpertId, setSelectedPartnerExpertId] = useState(null)
  const [clients, setClients] = useState([])
  const [selectedClientIds, setSelectedClientIds] = useState([])
  const [showAddPartnerExpertModal, setShowAddPartnerExpertModal] = useState(false)

  const fetchPartnerExperts = async () => {
    setLoading(true)
    const data = await getExpertPartnersList()
    setPartnerExperts(
      data.partnerExperts.map((provider) => {
        const clients = provider.clients || []
        return {
          ...provider,
          provider_id: provider.id,
          clients: { provider_id: provider.id, clients }
        }
      }) || []
    )
    setLoading(false)
  }

  const fetchAllClients = async () => {
    const data = await getClients()
    setClients(data.clients || [])
  }

  const fetchPartnerExpertAccess = async () => {
    setAccessLoading(true)
    try {
      const data = await getPartnerExpertAccess()
      const records = Array.isArray(data)
        ? data
        : data.accesses ||
          data.partner_expert_access ||
          (data.data && (data.data.accesses || data.data.partner_expert_access)) ||
          data.data ||
          []
      const sortedRecords = [...(records || [])].sort(
        (a, b) => Number(a.id || 0) - Number(b.id || 0)
      )
      setAccessManagement(sortedRecords)
    } finally {
      setAccessLoading(false)
    }
  }

  const onClickAddClient = (partnerExpertId) => {
    setSelectedPartnerExpertId(partnerExpertId)
  }

  const onClickAddClientAccessToPartnerExpert = async () => {
    const data = await addClientAccessToExpertPartner({
      service_provider_id: selectedPartnerExpertId,
      client_ids: selectedClientIds
    })
    if (data.done) {
      fetchPartnerExperts()
    }
    setSelectedPartnerExpertId(null)
    setSelectedClientIds([])
  }

  const onClickAddPartnerExpert = async () => {
    const data = await addExpertPartner({
      client_ids: selectedClientIds
    })
    if (data.done) {
      fetchPartnerExperts()
    }
    setSelectedClientIds(null)
    setShowAddPartnerExpertModal(false)
  }

  const onClickRemoveClientAccessFromPartnerExpert = async (
    partnerExpertId,
    clientName
  ) => {
    setLoading(true)
    const client = partnerExperts
      .find((sp) => sp.id === partnerExpertId)
      .clients.clients.find((c) => c.name === clientName)
    await removeClientAccessFromExpertPartner({
      service_provider_id: partnerExpertId,
      client_ids: [client.id]
    })
    fetchPartnerExperts()
  }

  const onToggleAccessEnabled = async (id, feature_name, enabled) => {
    const prevAccessManagement = [...(accessManagement || [])]

    setAccessManagement((prev) =>
      (prev || []).map((item) =>
        Number(item.id) === Number(id) ? { ...item, enabled } : item
      )
    )

    setUpdatingAccessIds((prev) => [...prev, id])
    try {
      await updatePartnerExpertAccess({ feature_name, enabled })
      await fetchPartnerExpertAccess()
    } catch (error) {
      setAccessManagement(prevAccessManagement)
      message.error('Unable to update access. Please try again.')
    } finally {
      setUpdatingAccessIds((prev) => prev.filter((item) => item !== id))
    }
  }

  useEffect(() => {
    fetchPartnerExperts()
    fetchPartnerExpertAccess()
    fetchAllClients()
  }, [])

  const columns = [
    { title: 'Partner Expert Id', dataIndex: 'provider_id', key: 'provider_id' },
    {
      title: 'Partner Expert Name',
      dataIndex: 'partner_expert_name',
      key: 'partner_expert_name'
    },
    {
      title: 'Clients',
      dataIndex: 'clients',
      key: 'clients',
      width: '70%',
      render: ({ clients, provider_id }) => (
        <div className='clients-list'>
          {clients.map((client) => (
            <div className='client-name'>
              {client.name}{' '}
              <span
                className='remove-icon'
                onClick={() =>
                  onClickRemoveClientAccessFromPartnerExpert(
                    provider_id,
                    client.name
                  )
                }
              >
                x
              </span>
            </div>
          ))}
        </div>
      )
    },
    {
      key: 'actions',
      title: 'Actions',
      render: (text, record) => {
        return (
          <div>
            <Button type='default' onClick={() => onClickAddClient(record.id)}>
              <PlusOutlined />
            </Button>
          </div>
        )
      }
    }
  ]

  const accessColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      render: (value) => value || '-'
    },
    {
      title: 'Feature Name',
      dataIndex: 'feature_name',
      key: 'feature_name',
      render: (value) => value || '-'
    },
    {
      title: 'Enabled',
      dataIndex: 'enabled',
      key: 'enabled',
      render: (value, record) => (
        <Switch
          checked={!!value}
          loading={updatingAccessIds.includes(record.id)}
          disabled={updatingAccessIds.includes(record.id)}
          onChange={(checked) =>
            onToggleAccessEnabled(record.id, record.feature_name, checked)
          }
        />
      )
    }
  ]

  return (
    <div className={styles}>
      <PHSHeader hideBreadCrumb={true} hideSubHeader={true} />
      <div className='container'>
        <div className='flex'>
          <h2>Partner Expert</h2>
          <div>
            <Button
              type='default'
              onClick={() => setShowAddPartnerExpertModal(true)}
            >
              <PlusOutlined />
            </Button>
          </div>
        </div>
        <div>
          <DataTable
            columns={columns || []}
            dataSource={partnerExperts || []}
            loading={loading}
            hidePagination={true}
          />
        </div>

        <div style={{ marginTop: 56 }}>
          <h2>Access Management</h2>
          <div style={{ fontSize: 12 }}>
            <DataTable
              columns={accessColumns || []}
              dataSource={accessManagement || []}
              loading={accessLoading}
              hidePagination={true}
              rowKey={(record, index) => record.id || `access-${index}`}
            />
          </div>
        </div>

        <Modal
          open={selectedPartnerExpertId !== null}
          onCancel={() => setSelectedPartnerExpertId(null)}
          onOk={onClickAddClientAccessToPartnerExpert}
        >
          <div>
            <div>Add Clients to Partner Expert</div>
            <div>
              <div>Select Clients</div>
              <Select
                mode='multiple'
                style={{ width: '100%' }}
                showSearch
                defaultValue={[]}
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
                onChange={(selectedClients) => {
                  setSelectedClientIds(selectedClients.map((id) => parseInt(id)))
                }}
              >
                {clients.map((client) => {
                  return (
                    <Option key={client.id} value={client.id}>
                      {client.full_name}
                    </Option>
                  )
                })}
              </Select>
            </div>
          </div>
        </Modal>
        <Modal
          open={showAddPartnerExpertModal}
          onCancel={() => setShowAddPartnerExpertModal(false)}
          onOk={onClickAddPartnerExpert}
        >
          <div>
            <div>Add Partner Expert</div>
            <div>
              <div>Select Provider</div>
              <Select
                mode='multiple'
                style={{ width: '100%' }}
                showSearch
                defaultValue={[]}
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
                onChange={(selectedClients) => {
                  setSelectedClientIds(selectedClients.map((id) => parseInt(id)))
                }}
              >
                {clients.map((client) => {
                  return (
                    <Option key={client.id} value={client.id}>
                      {client.full_name}
                    </Option>
                  )
                })}
              </Select>
            </div>
          </div>
        </Modal>
      </div>
    </div>
  )
}
